import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import modalStack from "aldoo-ra/Modal/ModalStack.js"
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import Img from "./Img"
import ProductPurchaseUI from "./ProductPurchaseUI"
import { displayName, getYoutubeId } from "../utils/productUtils"
import { getImageURL, catalogSizedUrl } from "../utils/images"
import { useProfileName } from "../hooks/useCatalogData"
import Chip from "./Chip"

const isVideo = (item) => item && typeof item === "object" && item.type === "video"

function VideoFrame({ url, className = "" }) {
  const id = getYoutubeId(url)
  if (!id) return null
  return (
    <iframe
      className={className}
      src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&enablejsapi=1`}
      title="Video"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  )
}

/**
 * Store product gallery: main image (or inline YouTube trailer), thumbnail
 * strip, fullscreen lightbox with keyboard/swipe navigation.
 *
 * The lightbox keeps the purchase bar the old site had at the top of it —
 * thumbnail, title and the price with Buy — because that is where a visitor
 * decides: they open a screenshot to look closely, and the way to buy should
 * not be a scroll away behind the overlay. The image fits what is left of the
 * screen below it.
 */
// `placement` / `onAdded`: the lightbox's buy button as its opener wants it — where the add is counted from,
// and what follows it instead of the cart opening (a panel over a checkout closes and leaves it there)
export default function ProductGallery({ images = [], productName = "", product = null, accent = "primary", placement = "product_gallery", onAdded = null }) {
  const [selected, setSelected] = useState(0)
  const [open, setOpen] = useState(false)
  // the lightbox above whatever is open (a checkout, a panel over it): the store's modal stack gives it the
  // next layer, and Escape closes it before what is under it
  const [layer, setLayer] = useState(null)
  useEffect(() => {
    if (!open) return
    const id = `product-gallery-${Math.random().toString(36).slice(2)}`
    setLayer(modalStack.push(id, () => setOpen(false), true))
    return () => {
      modalStack.remove(id)
      setLayer(null)
    }
  }, [open])
  // what kind of thing it is, under the title of the lightbox's purchase bar
  const contentTypeLabel = useProfileName(product?.profile)
  // the page behind the lightbox does not scroll while it is open
  useEffect(() => {
    if (!open || typeof document === "undefined") return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])
  const [touchX, setTouchX] = useState(null)
  const stripRef = useRef(null)
  // the selected thumbnail kept in view in the strip (its arrows step through the images), the page itself not scrolled
  useEffect(() => {
    const strip = stripRef.current
    const thumb = strip?.children[selected]
    if (!thumb) return
    const left = thumb.offsetLeft - strip.offsetLeft
    if (left < strip.scrollLeft) strip.scrollTo({ left, behavior: "smooth" })
    else if (left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollTo({ left: left + thumb.offsetWidth - strip.clientWidth, behavior: "smooth" })
  }, [selected])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false)
      if (e.key === "ArrowRight") setSelected((i) => (i + 1) % images.length)
      if (e.key === "ArrowLeft") setSelected((i) => (i - 1 + images.length) % images.length)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, images.length])

  if (!images.length) return null
  const current = images[selected]
  const next = () => setSelected((i) => (i + 1) % images.length)
  const prev = () => setSelected((i) => (i - 1 + images.length) % images.length)

  const thumbFor = (item) =>
    // the strip shows small thumbnails: ask the media mount for a small variant
    isVideo(item) ? `https://img.youtube.com/vi/${getYoutubeId(item.url)}/hqdefault.jpg` : catalogSizedUrl(getImageURL(item), 320)

  return (
    <div className="w-full flex flex-col gap-2.5 md:gap-5">
      <div
        // the same 180:222 box as the cards, so the picture keeps its shape from the list to the page
        className="relative w-full aspect-[180/222] md:aspect-[1/1] overflow-hidden bg-paper rounded-xl cursor-zoom-in"
        onClick={() => !isVideo(current) && setOpen(true)}
        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX === null) return
          const dx = e.changedTouches[0].clientX - touchX
          setTouchX(null)
          if (Math.abs(dx) < 40) return
          dx < 0 ? next() : prev()
        }}
      >
        {isVideo(current) ? (
          <VideoFrame url={current.url} className="absolute inset-0 w-full h-full" />
        ) : (
          <Img src={current} alt={productName} fit="contain" className="w-full h-full" loading="eager" fetchPriority="high" sizes="(min-width: 768px) 650px, 100vw" />
        )}
        {/* phones: a dot per picture over the bottom of the image, on a soft
            pill — the strip of thumbnails is desktop's, it costs a phone too
            much height; swiping the image moves between pictures */}
        {images.length > 1 && (
          <div className="md:hidden absolute bottom-3 inset-x-0 flex justify-center pointer-events-none" data-testid="gallery-dots">
            <div className="flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1.5 pointer-events-auto">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`${i + 1} / ${images.length}`}
                  onClick={(e) => { e.stopPropagation(); setSelected(i) }}
                  className={twMerge("h-2 rounded-full bg-white/60 transition-all", i === selected ? "w-4 bg-white" : "w-2")}
                />
              ))}
            </div>
          </div>
        )}
        {images.length > 1 && (
          <>
            <button type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); prev() }} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hidden tab:flex items-center justify-center hover:bg-white">
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); next() }} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hidden tab:flex items-center justify-center hover:bg-white">
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="relative hidden md:block">
        <div ref={stripRef} className="flex flex-row gap-3 overflow-x-auto no-scrollbar py-1">
          {images.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSelected(i)}
              // four to a row on a phone, as the old strip was; more as it widens
              className={twMerge("relative shrink-0 aspect-[180/222] w-[calc((100%-36px)/4)] tab:w-[calc((100%-48px)/5)] md:w-[calc((100%-60px)/6)] lg:w-[calc((100%-72px)/7)] overflow-hidden border-2 transition-opacity", i === selected ? `${accent === "text" ? "border-text" : "border-primary"} opacity-100` : "border-transparent opacity-60 hover:opacity-100")}
            >
              <img src={thumbFor(item)} alt="" className="w-full h-full object-contain" loading="lazy" />
              {isVideo(item) && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <span className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                    <span className="ml-0.5 border-y-[6px] border-y-transparent border-l-[10px] border-l-black" />
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>
        {images.length > 4 && (
          <>
            <button type="button" aria-label="Previous" onClick={() => setSelected((i) => (i - 1 + images.length) % images.length)} className="absolute -left-[15px] top-1/2 -translate-y-1/2 z-10 w-[30px] h-[30px] rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[24px] font-bold leading-none">‹</button>
            <button type="button" aria-label="Next" onClick={() => setSelected((i) => (i + 1) % images.length)} className="absolute -right-[15px] top-1/2 -translate-y-1/2 z-10 w-[30px] h-[30px] rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[24px] font-bold leading-none">›</button>
          </>
        )}
        </div>
      )}

      {/* on the body, above every layer of the page (the sticky header, the cart
          rail, a hovered card): inside the page it would sit in the gallery's own
          stacking context, and what comes later in the page would show through */}
      {open && typeof document !== "undefined" && createPortal(
        <div style={{ zIndex: layer ?? 2000 }} className="fixed inset-0 bg-paper flex flex-col" onClick={() => setOpen(false)}>
          {product && (
            <div
              className="shrink-0 w-full bg-paper shadow-[0_2px_8px_rgba(0,0,0,0.1)] px-4 tab:px-[50px] md:px-[80px] py-3 flex items-center gap-2 tab:gap-4"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-purchase-bar"
            >
              <div className="w-10 h-10 tab:w-[50px] tab:h-[50px] md:w-[60px] md:h-[60px] shrink-0 rounded overflow-hidden">
                <Img src={product.square || product.thumb} alt={productName} className="w-full h-full" />
              </div>
              {/* no room for the title on a phone: the thumbnail says which product */}
              <div className="hidden md:flex flex-1 min-w-0 flex-col items-start gap-1">
                <div className="w-full truncate text-[16px] md:text-[18px] font-semibold">{productName || displayName(product)}</div>
                {contentTypeLabel && <Chip tone="outline" size="sm" label={contentTypeLabel} to={`/type/${product.profile?.id}`} />}
              </div>
              <div className="flex-1 md:hidden" />
              <div className="shrink-0">
                <ProductPurchaseUI
                  product={product}
                  compact
                  placement={placement}
                  onAdded={onAdded ? () => {
                    setOpen(false)
                    onAdded()
                  } : null}
                />
              </div>
            </div>
          )}

          <div className="relative flex-1 min-h-0 flex items-center justify-center">
            <button type="button" aria-label="Close" className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-dark/80 hover:bg-dark text-white flex items-center justify-center shadow-md" onClick={() => setOpen(false)}>
              <XMarkIcon className="w-6 h-6" />
            </button>
            {/* round arrows at the screen's edges, quiet on the white page until the pointer is on them */}
            {images.length > 1 && (
              <>
                <button type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); prev() }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-dark/10 text-text hover:bg-dark/80 hover:text-white shadow-sm backdrop-blur-sm transition-colors hidden sm:flex items-center justify-center">
                  <ChevronLeftIcon className="w-7 h-7" />
                </button>
                <button type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); next() }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-dark/10 text-text hover:bg-dark/80 hover:text-white shadow-sm backdrop-blur-sm transition-colors hidden sm:flex items-center justify-center">
                  <ChevronRightIcon className="w-7 h-7" />
                </button>
              </>
            )}
            {/* the image fills what the bar leaves, never more: `contain` keeps
                it whole whatever the screen is */}
            <div
              className="w-full h-full flex items-center justify-center p-2 md:p-6"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (touchX === null) return
                const dx = e.changedTouches[0].clientX - touchX
                setTouchX(null)
                if (Math.abs(dx) < 40) return
                dx < 0 ? next() : prev()
              }}
            >
              {isVideo(current) ? (
                <VideoFrame url={current.url} className="w-full max-w-[1200px] aspect-video" />
              ) : (
                // `Img` wraps the picture in a block element: an image sized to
                // its own aspect ratio inside it would sit top-left, so the
                // wrapper centres it. A phone fills the screen with the picture
                // (scaled up, a cover is small); a wide screen shows it no
                // larger than it is
                <Img
                  src={current}
                  alt={productName}
                  fit="contain"
                  sizes="100vw"
                  className="w-full h-full flex items-center justify-center"
                  imgClassName="w-full h-full md:w-auto md:h-auto md:max-w-full md:max-h-full object-contain"
                  loading="eager"
                />
              )}
            </div>
          </div>

          {images.length > 1 && (
            <div className="shrink-0 w-full py-2 text-center text-[14px] sm:text-[16px] font-medium bg-paper" data-testid="gallery-counter">
              {selected + 1} / {images.length}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
